import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  ArrowRight,
  Briefcase,
  Compass,
  Eye,
  FileSearch,
  Handshake,
  Lock,
  Mail,
  Search,
  ShieldCheck,
  Sparkles,
  Target,
  UserPlus,
  Users,
} from "lucide-react";
import { SpnLogo } from "@/components/brand/spn-logo";
import { useGetMe } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { getFunnelContext, getLoginHref, trackFunnelEvent } from "@/lib/funnel-analytics";

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Verified agency experience",
    body: "Publish past performance with tiered visibility controls — public, limited, or private — so partners see proof, not promises.",
  },
  {
    icon: Sparkles,
    title: "AI-assisted profiles",
    body: "Paste a capability statement and let AI extract your capabilities, NAICS codes, and agency experience for review — no retyping.",
  },
  {
    icon: Compass,
    title: "Discover any registered vendor",
    body: "Search roughly 889,000 active SAM.gov registrants — on SPN or not — by name, UEI, CAGE, NAICS, state, or certifications, with clear data-freshness notes.",
  },
  {
    icon: Target,
    title: "Automatic opportunity matching",
    body: "Federal opportunities sync daily and are scored against your profile with weighted, AI-adjusted scoring that factors in deadlines.",
  },
  {
    icon: Users,
    title: "Partner teaming analysis",
    body: "Build a partner roster and see gap-fill analysis plus solo, prime/sub, and three-way arrangement recommendations with clear tier badges.",
  },
  {
    icon: Eye,
    title: "Watchlist & vendor invites",
    body: "Track promising off-network vendors on a watchlist and bring them aboard with one-click branded email invites.",
  },
  {
    icon: Handshake,
    title: "Teaming posts & messaging",
    body: "Post what you're pursuing and what you need, then take a promising match into a private workspace-to-workspace conversation.",
  },
  {
    icon: Mail,
    title: "Team workspaces",
    body: "Invite coworkers into your workspace by email so your whole BD team works from one shared profile and pipeline.",
  },
];

const STEPS = [
  {
    icon: UserPlus,
    title: "Request access",
    body: "Tell us about your firm. An admin reviews your request — usually within a business day — and you sign in with a magic link. No passwords.",
  },
  {
    icon: FileSearch,
    title: "Build your profile",
    body: "Enter your capabilities and agency experience manually, or upload a capability statement and let AI do the heavy lifting.",
  },
  {
    icon: Search,
    title: "Find your team",
    body: "Discover vendors across the entire SAM.gov registrant base, review the opportunities matched to you every day, and start conversations that turn into wins.",
  },
];

function PromoVideo() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== "spn-promo-video") return;
      if (event.data.event === "start") trackFunnelEvent("video_start", "landing");
      if (event.data.event === "complete") trackFunnelEvent("video_complete", "landing");
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <div
      className="relative mx-auto mt-14 w-full max-w-4xl overflow-hidden rounded-xl border border-border bg-card shadow-lg"
      style={{ aspectRatio: "16 / 9" }}
      data-testid="landing-promo-video"
    >
      {!loaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-[#3B82F6]/10 via-background to-[#1D4ED8]/10">
          <SpnLogo className="h-12 w-12" />
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner className="h-4 w-4 text-primary" />
            Loading video…
          </div>
        </div>
      )}
      <iframe
        src="/spn-promo/"
        title="Synergistic Partner Network — product overview video"
        className={`absolute inset-0 h-full w-full border-0 transition-opacity duration-500 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
        loading="lazy"
        allow="autoplay"
        onLoad={() => {
          setLoaded(true);
          trackFunnelEvent("video_load", "landing");
        }}
      />
    </div>
  );
}

export default function LandingPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading } = useGetMe();
  const variant = getFunnelContext().variant;

  useEffect(() => {
    trackFunnelEvent("landing_view", "landing");
  }, []);

  const accessHref = getLoginHref("request");
  const signInHref = getLoginHref("signin");
  const ctaClick = () => trackFunnelEvent("cta_click", "landing");

  useEffect(() => {
    document.title = "Synergistic Partner Network — GovCon Teaming Platform";
    let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "SPN helps government contractors publish verified agency experience, search ~889K SAM.gov registrants, get daily AI-scored opportunity matches, analyze partner teaming arrangements, and form winning teams.";
  }, []);

  useEffect(() => {
    if (me?.user) {
      navigate("/dashboard");
    }
  }, [me, navigate]);

  if (isLoading || me?.user) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-6 w-6 text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <SpnLogo className="h-8 w-8" />
            <div className="leading-tight">
              <div className="text-sm font-semibold">Synergistic Partner Network</div>
              <div className="hidden text-[10px] uppercase tracking-wider text-muted-foreground sm:block">
                GovCon Teaming Platform
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" asChild data-testid="header-signin">
               <Link href={signInHref}>Sign in</Link>
            </Button>
            <Button size="sm" asChild data-testid="header-request-access">
               <Link href={accessHref} onClick={ctaClick}>Request access</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 [background:radial-gradient(900px_circle_at_85%_-10%,hsl(217_91%_60%/0.15),transparent_60%),radial-gradient(700px_circle_at_-10%_110%,hsl(199_89%_48%/0.12),transparent_60%)]" />
        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
              <ShieldCheck className="h-3.5 w-3.5" />
              Built for US government contractors
            </div>
              <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
                {variant === "variant"
                  ? "Build a stronger team for your next federal pursuit."
                  : "Find the right teammates for your next federal pursuit."}
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
                {variant === "variant"
                  ? "SPN helps GovCon vendors prove agency experience, discover the right partners, and build teams backed by real gap-fill analysis."
                  : "SPN gives GovCon vendors one place to prove agency experience, search the entire SAM.gov registrant base, get federal opportunities matched to them automatically every day, and build teams backed by real gap-fill analysis."}
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button size="lg" className="w-full gap-2 sm:w-auto" asChild data-testid="hero-request-access">
                <Link href={accessHref} onClick={ctaClick}>
                  {variant === "variant" ? "Join the SPN network" : "Request access"}{" "}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="w-full sm:w-auto" asChild data-testid="hero-signin">
                <Link href={signInHref}>Sign in</Link>
              </Button>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Passwordless sign-in · Free to join · Every workspace is reviewed by a human
            </p>
          </div>
          <PromoVideo />
          <div className="mx-auto mt-5 max-w-2xl text-center text-sm text-muted-foreground">
            See how SPN turns evidence into better teaming conversations. When you’re ready,
            request free access — we review requests, usually within a business day.
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-border bg-muted/40">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 py-8 text-center sm:grid-cols-3 sm:px-6">
          <div className="flex items-center justify-center gap-3">
            <Briefcase className="h-5 w-5 shrink-0 text-primary" />
            <span className="text-sm text-muted-foreground">
              Search ~889K active SAM.gov registrants — on SPN or not
            </span>
          </div>
          <div className="flex items-center justify-center gap-3">
            <Lock className="h-5 w-5 shrink-0 text-primary" />
            <span className="text-sm text-muted-foreground">
              You control what each vendor sees about your experience
            </span>
          </div>
          <div className="flex items-center justify-center gap-3">
            <ShieldCheck className="h-5 w-5 shrink-0 text-primary" />
            <span className="text-sm text-muted-foreground">
              We review every workspace to keep the network trustworthy
            </span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Everything you need to team up and win
          </h2>
          <p className="mt-4 text-muted-foreground">
            From proving past performance to matching opportunities, SPN covers the full teaming
            lifecycle.
          </p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-lg border border-card-border bg-card p-6 shadow-sm"
            >
              <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="text-base font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-border bg-muted/40">
        <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">How it works</h2>
            <p className="mt-4 text-muted-foreground">
              Three steps from first visit to your first teaming conversation.
            </p>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative rounded-lg border border-card-border bg-card p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
                    {i + 1}
                  </div>
                  <s.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="text-base font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
        <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-primary px-6 py-14 text-center text-primary-foreground sm:px-12">
          <div className="pointer-events-none absolute inset-0 [background:radial-gradient(600px_circle_at_80%_-20%,hsl(199_89%_48%/0.35),transparent_60%)]" />
          <div className="relative">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Ready to build your next winning team?
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-primary-foreground/85">
              Join the network of GovCon vendors who team on evidence, not guesswork.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                size="lg"
                variant="secondary"
                className="w-full gap-2 sm:w-auto"
                asChild
                data-testid="cta-request-access"
              >
                <Link href={accessHref} onClick={ctaClick}>
                  Request access <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="w-full border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 sm:w-auto"
                asChild
                data-testid="cta-signin"
              >
                <Link href={signInHref}>Sign in</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <SpnLogo className="h-5 w-5" />
            © {new Date().getFullYear()} Synergistic Partner Network
          </div>
          <div className="flex items-center gap-4 text-sm">
             <Link href={signInHref} className="text-muted-foreground hover:text-foreground">
              Sign in
            </Link>
            <Link href={accessHref} onClick={ctaClick} className="text-muted-foreground hover:text-foreground">
              Request access
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
